/**
 * Derive the primary color shade family from a single brand hex color.
 *
 * The Tailwind theme (tailwind.config.js) resolves `primary`, `primary-deep`,
 * `primary-mild` and `primary-subtle` from the CSS variables `--primary*`.
 * Tenant branding supplies only one hex, so the shades are derived here and
 * written onto :root by BrandingContext.applyBranding.
 */

// The canonical brand primary — the single allowed hex literal for it.
// Everything else reads var(--primary) / Tailwind `primary`; import THIS
// when a runtime fallback is unavoidable (e.g. canvas charts).
export const BRAND_PRIMARY = '#12717A';

export interface PrimaryShades {
  primary: string;
  deep: string;
  mild: string;
  subtle: string;
  /** "r, g, b" triplet for rgba(var(--primary-rgb), a) consumers (Bootstrap bridge). */
  rgb: string;
}

const clamp = (n: number): number => Math.max(0, Math.min(255, Math.round(n)));

const parseHex = (hex: string): [number, number, number] | null => {
  const match = hex.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return null;
  let value = match[1];
  if (value.length === 3) {
    value = value.split('').map((c) => c + c).join('');
  }
  const num = parseInt(value, 16);
  return [(num >> 16) & 0xff, (num >> 8) & 0xff, num & 0xff];
};

const toHex = (r: number, g: number, b: number): string =>
  '#' + [r, g, b].map((c) => clamp(c).toString(16).padStart(2, '0')).join('');

/** Darken by mixing toward black. amount 0..1 */
export const darken = (hex: string, amount: number): string => {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const [r, g, b] = rgb;
  return toHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
};

/** Lighten by mixing toward white. amount 0..1 */
export const lighten = (hex: string, amount: number): string => {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const [r, g, b] = rgb;
  return toHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
};

/** Translucent version of the color, e.g. for subtle backgrounds. */
export const alpha = (hex: string, opacity: number): string => {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const [r, g, b] = rgb;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const derivePrimaryShades = (hex: string): PrimaryShades => {
  const rgb = parseHex(hex) || parseHex(BRAND_PRIMARY)!;
  return {
    primary: parseHex(hex) ? hex : BRAND_PRIMARY,
    deep: darken(hex, 0.15),
    mild: lighten(hex, 0.15),
    subtle: alpha(hex, 0.1),
    rgb: rgb.join(', '),
  };
};
